import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import type { Contact, CreateContactPayload, UpdateContactPayload } from '../types';

interface ContactFormModalProps {
  open: boolean;
  contact?: Contact | null; // null/undefined = create mode
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateContactPayload | UpdateContactPayload) => void;
}

interface FormState {
  name: string;
  company: string;
  position: string;
  email: string;
  phone: string;
  notes: string;
}

const EMPTY: FormState = { name: '', company: '', position: '', email: '', phone: '', notes: '' };

function contactToForm(c: Contact): FormState {
  return {
    name: c.name ?? '',
    company: c.company ?? '',
    position: c.position ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    notes: c.notes ?? '',
  };
}

export function ContactFormModal({ open, contact, loading, onClose, onSubmit }: ContactFormModalProps) {
  const isEdit = !!contact;
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(contact ? contactToForm(contact) : EMPTY);
  }, [open, contact]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit({
      name: form.name.trim(),
      company: form.company.trim(),
      position: form.position.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      notes: form.notes.trim(),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Contact' : 'New Contact'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="contact-form" loading={loading}>
            {isEdit ? 'Save Changes' : 'Create Contact'}
          </Button>
        </>
      }
    >
      <form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name *"
          name="contact-name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="Full name…"
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Company"
            name="contact-company"
            value={form.company}
            onChange={(e) => set('company', e.target.value)}
            placeholder="Company…"
          />
          <Input
            label="Position"
            name="contact-position"
            value={form.position}
            onChange={(e) => set('position', e.target.value)}
            placeholder="Job title…"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Email"
            name="contact-email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="email@example.com"
          />
          <Input
            label="Phone"
            name="contact-phone"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="Phone…"
          />
        </div>

        <div>
          <label htmlFor="contact-notes" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Notes
          </label>
          <textarea
            id="contact-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Notes…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </form>
    </Modal>
  );
}
